// src/oura/newscan/flags.ts
//
// The two New scan switches, both explicit `=== "true"`, absent means OFF:
//
//   VITE_ENABLE_OURA_SCAN_DRAFT        the new flow exists (routes and entry
//                                      points). Off: today's NewScanModal, unchanged.
//   VITE_ENABLE_OURA_SCAN_DRAFT_LIVE   the draft service calls the backend.
//
// Without the live switch, a local dev run (VITE_APP_ENV === "development")
// serves the fixture engine and every other environment answers `absent`, so
// no sample business ever reaches a staging or production screen.
//
// Deliberately not keyed on VITE_ENABLE_OURA_LIVE_API: that one is true on
// staging and production, where the /scan-drafts routes do not exist yet.

import { isOuraProd } from "@oura/role";

export type ScanDraftMode = "live" | "fixtures" | "absent";

/** The new flow is switched on: routes and entry points show it. */
export const SCAN_DRAFT_UI_ON: boolean = import.meta.env.VITE_ENABLE_OURA_SCAN_DRAFT === "true";

/** The draft service talks to the backend. */
const SCAN_DRAFT_LIVE: boolean = import.meta.env.VITE_ENABLE_OURA_SCAN_DRAFT_LIVE === "true";

/**
 * First AI check: one "Which branches?" list in place of the outlet cards,
 * and "Tell Oura" for the same answer in plain words (Jack, 19 Oct 2026).
 * On in dev and staging; production waits for the flag.
 */
export const BRANCHES_CARD_ON: boolean = import.meta.env.VITE_ENABLE_OURA_BRANCHES_CARD === "true" || !isOuraProd();

const LOCAL_DEV: boolean = import.meta.env.VITE_APP_ENV === "development";

/** Where the draft calls go: the backend, the local fixture engine, or nowhere. */
export function scanDraftMode(): ScanDraftMode {
    if (SCAN_DRAFT_LIVE) return "live";
    return LOCAL_DEV ? "fixtures" : "absent";
}

/** How often a building draft is read again. */
export function scanDraftPollMs(): number {
    return scanDraftMode() === "live" ? 3000 : 1500;
}

/**
 * Area names (Jack, 17 Oct): one venue known by several area names, kept as `alsoSearchedAs` on
 * its one outlet. Needs the backend to keep the field and draft location questions under each
 * name, so it has its own switch: VITE_ENABLE_OURA_AREA_NAMES_LIVE exactly "true". A local dev
 * run on the fixture engine shows it too.
 */
export const OURA_AREA_NAMES_ENABLED: boolean =
    SCAN_DRAFT_UI_ON && (scanDraftMode() === "fixtures" || (scanDraftMode() === "live" && import.meta.env.VITE_ENABLE_OURA_AREA_NAMES_LIVE === "true"));
